import React, { PropsWithChildren, forwardRef } from 'react';

import styles from './Button.module.css';

type ButtonVariant = 'success' | 'soft' | 'danger' | 'primary';

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
};

const btnClasses = {
  success: 'btn-success',
  soft: 'btn-primary-soft',
  danger: 'btn-danger-soft',
  primary: 'btn-primary',
};

const btnIcons = {
  success: 'fe fe-check',
  soft: 'fe fe-skip-forward',
  danger: 'fe fe-slash',
};

/**
 * Helper function to get the appropriate button icon based on the button variant
 *
 * @param variant ButtonVariant
 */
export const getButtonIcon = (variant?: ButtonVariant): JSX.Element | null =>
  variant && variant !== 'primary' ? <span className={btnIcons[variant]} /> : null;

/**
 * Button component, with `variant` prop to choose between different button types.
 * Gives a ref to the underlying button element.
 * Accepts children of any kind. If there is an icon in the button (because of a button variant),
 * the children will have some left margin added to space it apart.
 *
 * @param props ButtonProps
 */
const Button = forwardRef<HTMLButtonElement, PropsWithChildren<ButtonProps>>(
  ({ variant, children, className = 'font-weight-bold', ...rest }: PropsWithChildren<ButtonProps>, ref) => {
    const classNames = `btn ${styles.btn} ${variant ? btnClasses[variant] : 'btn-white'}${
      className ? ` ${className}` : ''
    } `;
    return (
      <button className={classNames} ref={ref} {...rest}>
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
